/* eslint-disable max-len */
/**
 * Firmware OTA Tutorial chapters (PRJ-07 / LLD UC 10). 5 chapters covering
 * firmware update flow. Authored in Vietnamese per Phase 1 D-11 locale default.
 *
 * Total: 5 chapters × 4 sections each = 20 SVG illustrations.
 *
 * Wave 0 (03-01): 5 metadata stubs.
 * Wave 5 (03-08): this file — full content + 20 SVG imports.
 *
 * eslint-disable max-len: section body strings are Vietnamese tutorial
 * content that should not be artificially broken mid-sentence for line length.
 */
import fw01Why from '../../../components/mstem-tutorial-modal/illustrations/fw-01-overview-why.svg';
import fw01Versions from '../../../components/mstem-tutorial-modal/illustrations/fw-01-overview-versions.svg';
import fw01Flow from '../../../components/mstem-tutorial-modal/illustrations/fw-01-overview-flow.svg';
import fw01Safe from '../../../components/mstem-tutorial-modal/illustrations/fw-01-overview-safe.svg';
import fw02Cable from '../../../components/mstem-tutorial-modal/illustrations/fw-02-usb-cable.svg';
import fw02Port from '../../../components/mstem-tutorial-modal/illustrations/fw-02-port-check.svg';
import fw02Battery from '../../../components/mstem-tutorial-modal/illustrations/fw-02-battery-50.svg';
import fw02Close from '../../../components/mstem-tutorial-modal/illustrations/fw-02-close-apps.svg';
import fw03Button from '../../../components/mstem-tutorial-modal/illustrations/fw-03-upload-button.svg';
import fw03Step1 from '../../../components/mstem-tutorial-modal/illustrations/fw-03-wizard-step1.svg';
import fw03Step2 from '../../../components/mstem-tutorial-modal/illustrations/fw-03-wizard-step2.svg';
import fw03Progress from '../../../components/mstem-tutorial-modal/illustrations/fw-03-progress-bar.svg';
import fw04Icon from '../../../components/mstem-tutorial-modal/illustrations/fw-04-wifi-icon.svg';
import fw04Badge from '../../../components/mstem-tutorial-modal/illustrations/fw-04-future-badge.svg';
import fw04Flow from '../../../components/mstem-tutorial-modal/illustrations/fw-04-ota-flow.svg';
import fw04Proscons from '../../../components/mstem-tutorial-modal/illustrations/fw-04-pros-cons.svg';
import fw05Warning from '../../../components/mstem-tutorial-modal/illustrations/fw-05-disconnect-warning.svg';
import fw05Retry from '../../../components/mstem-tutorial-modal/illustrations/fw-05-retry-button.svg';
import fw05Reset from '../../../components/mstem-tutorial-modal/illustrations/fw-05-factory-reset.svg';
import fw05Success from '../../../components/mstem-tutorial-modal/illustrations/fw-05-success-check.svg';

const FIRMWARE_CHAPTERS = [
    {
        id: 'fw-01-overview',
        title: 'Tổng quan cập nhật firmware',
        sections: [
            {
                heading: 'Vì sao cần cập nhật firmware?',
                body: 'Firmware là phần mềm chạy trên vi điều khiển robot/kit. Cập nhật firmware giúp sửa lỗi, thêm tính năng mới, và tương thích với phiên bản mSTEM IDE mới hơn.',
                illustration: fw01Why,
                illustrationAlt: 'So sánh robot firmware cũ có lỗi vs firmware mới đã sửa lỗi'
            },
            {
                heading: 'Tương thích phiên bản',
                body: 'Mỗi model robot/kit hỗ trợ một dải firmware version. Ví dụ: MBF Robot v1 hỗ trợ firmware v1.0 - v1.5; MBF Robot v2 hỗ trợ v2.0+. Chọn đúng version trong wizard cập nhật.',
                illustration: fw01Versions,
                illustrationAlt: 'Bảng tương thích version giữa các model robot và firmware'
            },
            {
                heading: 'Quy trình tổng quát',
                body: 'Quy trình cập nhật firmware gồm 4 bước: (1) chọn robot/kit cần cập nhật, (2) chọn version firmware, (3) chờ progress bar tới 100%, (4) thông báo thành công. Toàn bộ quá trình mất khoảng 30-60 giây.',
                illustration: fw01Flow,
                illustrationAlt: 'Sơ đồ 4 bước quy trình cập nhật firmware'
            },
            {
                heading: 'Lưu ý an toàn',
                body: 'KHÔNG rút cáp USB hoặc tắt nguồn khi đang upload. Đảm bảo pin robot ≥ 50%. Backup dự án Scratch quan trọng trước khi cập nhật để có thể phục hồi nếu cần.',
                illustration: fw01Safe,
                illustrationAlt: 'Cảnh báo 3 quy tắc an toàn khi cập nhật firmware'
            }
        ]
    },
    {
        id: 'fw-02-prep-device',
        title: 'Chuẩn bị thiết bị',
        sections: [
            {
                heading: 'Kết nối cáp USB',
                body: 'Sử dụng cáp USB chuẩn (micro-USB cho MBF Robot v1, Type-C cho v2 và Kit SmartHome) kết nối laptop ↔ robot. Đèn LED nguồn trên robot sáng nếu cấp nguồn OK.',
                illustration: fw02Cable,
                illustrationAlt: 'Sơ đồ laptop kết nối với robot qua cáp USB'
            },
            {
                heading: 'Kiểm tra cổng COM',
                body: 'Mở Device Manager (Windows) hoặc System Information (macOS) → tìm tên thiết bị "USB-SERIAL CH340" hoặc "CP2102". Ghi nhớ tên cổng (ví dụ COM3 hoặc /dev/cu.usbserial-1234) để chọn trong wizard.',
                illustration: fw02Port,
                illustrationAlt: 'Device Manager hiển thị danh sách cổng COM với COM3 được highlight'
            },
            {
                heading: 'Kiểm tra pin',
                body: 'Pin robot phải ≥ 50% để đảm bảo không hết pin giữa chừng. Đèn LED pin nhấp nháy đỏ = pin yếu. Sạc đầy hoặc cắm trực tiếp adapter trước khi cập nhật.',
                illustration: fw02Battery,
                illustrationAlt: 'Biểu tượng pin với mức 50% tối thiểu cho cập nhật an toàn'
            },
            {
                heading: 'Đóng các app khác',
                body: 'Đóng Arduino IDE, Serial Monitor, PuTTY, hoặc bất kỳ app nào đang giữ cổng COM. Nếu không đóng, IDE sẽ báo lỗi "Cổng COM đang được sử dụng" và không thể upload.',
                illustration: fw02Close,
                illustrationAlt: 'Danh sách các app cần đóng (Arduino IDE, PuTTY, VS Code Serial Monitor)'
            }
        ]
    },
    {
        id: 'fw-03-usb-update',
        title: 'Cập nhật qua cáp USB',
        sections: [
            {
                heading: 'Click nút "Upload Firmware"',
                body: 'Trên menu bar, click nút "Upload Firmware" (icon mũi tên hướng lên). Wizard 3 bước sẽ mở ra để hướng dẫn bạn cập nhật firmware cho robot đã chọn.',
                illustration: fw03Button,
                illustrationAlt: 'Nút Upload Firmware được highlight trên menu bar'
            },
            {
                heading: 'Bước 1: Chọn robot',
                body: 'Wizard step 1 hiện danh sách robot đã đăng ký trong Robot Manager. Chọn robot bạn muốn cập nhật. Nếu chưa có robot nào, vào Robot Manager để thêm trước khi quay lại wizard.',
                illustration: fw03Step1,
                illustrationAlt: 'Wizard bước 1 hiển thị danh sách robot có sẵn để chọn'
            },
            {
                heading: 'Bước 2: Chọn version firmware',
                body: 'Wizard step 2 hiện danh sách firmware version tương thích với robot đã chọn. Mặc định "Latest stable" (v1.5). Có thể chọn version cũ hơn nếu cần rollback do firmware mới bị lỗi.',
                illustration: fw03Step2,
                illustrationAlt: 'Wizard bước 2 cho chọn phiên bản firmware: v1.5 mới nhất hoặc v1.4/v1.3 cũ'
            },
            {
                heading: 'Bước 3: Theo dõi progress',
                body: 'Click "Bắt đầu upload" → progress bar chạy từ 0% → 100% trong ~30 giây. Khi xong, thông báo "Cập nhật firmware thành công" hiện ra và robot tự khởi động lại với firmware mới.',
                illustration: fw03Progress,
                illustrationAlt: 'Progress bar hiển thị 60% với log đang ghi firmware'
            }
        ]
    },
    {
        id: 'fw-04-ota-wifi',
        title: 'Cập nhật qua Wi-Fi (OTA)',
        sections: [
            {
                heading: 'Tính năng OTA — Coming soon',
                body: 'Cập nhật firmware qua Wi-Fi (OTA - Over The Air) chưa được hỗ trợ ở phiên bản prototype này. Tính năng sẽ có ở v2 sau khi BE team tích hợp module ESP32 và backend server.',
                illustration: fw04Icon,
                illustrationAlt: 'Biểu tượng Wi-Fi với badge Coming Soon'
            },
            {
                heading: 'Mockup UI dự kiến',
                body: 'Khi tính năng sẵn sàng, menu Upload Firmware sẽ có thêm tùy chọn "Cập nhật qua Wi-Fi" bên cạnh "Cập nhật qua USB". UI sẽ tương tự wizard 3 bước hiện tại, chỉ thay phương thức truyền dữ liệu.',
                illustration: fw04Badge,
                illustrationAlt: 'Mockup UI tương lai với 2 tùy chọn USB và Wi-Fi (Wi-Fi disabled)'
            },
            {
                heading: 'Quy trình OTA',
                body: 'Robot kết nối tới router Wi-Fi cùng mạng với laptop → laptop gửi firmware qua mạng nội bộ → robot tự download và update. Không cần cáp USB cắm trực tiếp giữa laptop và robot.',
                illustration: fw04Flow,
                illustrationAlt: 'Sơ đồ luồng OTA: Laptop → Router Wi-Fi → Robot ESP32'
            },
            {
                heading: 'Ưu/nhược điểm',
                body: 'Ưu: không cần cáp, có thể cập nhật nhiều robot đồng thời trong cùng mạng, tiết kiệm cổng USB ở lớp học đông. Nhược: cần có Wi-Fi router, chậm hơn USB (~2-3 phút), cần module ESP32 hỗ trợ.',
                illustration: fw04Proscons,
                illustrationAlt: 'Bảng so sánh ưu nhược điểm của OTA vs USB'
            }
        ]
    },
    {
        id: 'fw-05-troubleshoot',
        title: 'Xử lý sự cố',
        sections: [
            {
                heading: 'Mất kết nối giữa chừng',
                body: 'Nếu cáp USB bị rút ra giữa upload, robot có thể vào trạng thái "brick" (treo). Đèn LED nguồn vẫn sáng nhưng robot không phản hồi lệnh và mSTEM IDE báo "Mất kết nối Serial". Cần factory reset để phục hồi.',
                illustration: fw05Warning,
                illustrationAlt: 'Cảnh báo đỏ mất kết nối giữa chừng với robot vào trạng thái brick'
            },
            {
                heading: 'Thử lại upload',
                body: 'Click nút "Retry" trong wizard. IDE sẽ kiểm tra lại cổng COM + thử upload lại tự động. Nếu thất bại liên tiếp 3 lần, chuyển sang bước factory reset để đưa robot về chế độ bootloader.',
                illustration: fw05Retry,
                illustrationAlt: 'Dialog báo lỗi TIMEOUT với nút Retry và Hủy'
            },
            {
                heading: 'Factory reset',
                body: 'Nhấn giữ nút Reset trên robot (góc phải board, gần module ESP32) trong 10 giây cho tới khi tất cả LED tắt. Sau đó, robot vào chế độ bootloader, sẵn sàng nhận firmware mới. Quay lại Upload Firmware wizard để upload lại.',
                illustration: fw05Reset,
                illustrationAlt: 'Sơ đồ board robot với nút Reset được highlight cho factory reset'
            },
            {
                heading: 'Xác nhận thành công',
                body: 'Sau khi upload thành công, LED RGB trên robot nhấp nháy 3 lần xanh và robot tự khởi động lại. Test bằng cách upload chương trình Scratch đơn giản (di chuyển 10cm) — robot phải thực hiện đúng để xác nhận firmware OK.',
                illustration: fw05Success,
                illustrationAlt: 'Biểu tượng dấu tích xanh báo cập nhật thành công với checklist xác nhận'
            }
        ]
    }
];

export default FIRMWARE_CHAPTERS;
